import type { PropertyOwnerKycRow } from '../types/propertyOwnerTypes'

/**
 * The KYC Review queue serves both intake paths — an owner who verified
 * themselves through the seller-web wizard, and an owner whose documents an
 * operator uploaded on their behalf. These constants are what let one queue
 * read clearly for both.
 */

// The statuses a human decision is still owed on. 'pending' covers admin-created
// owners whose submission is bootstrapped at PENDING by the create flow;
// 'user_submitted' and 'pending_kyc_review' are the SumSub-driven lanes.
export const KYC_ACTIONABLE_STATUSES: readonly string[] = [
  'pending',
  'user_submitted',
  'pending_kyc_review',
]

// `kyc_vendor` as written by whichever service opened the submission:
// seller-service writes 'sumsub', admin-service writes 'admin'.
const SUMSUB_VENDOR = 'sumsub'
const ADMIN_VENDOR = 'admin'

export function kycIntakeSourceLabel(kycVendor: string | null): string {
  if (kycVendor === SUMSUB_VENDOR) return 'Self-serve'
  if (kycVendor === ADMIN_VENDOR) return 'Admin upload'
  return '—'
}

/**
 * What happened to a settled submission, in the operator's terms.
 *
 * `decisionSource` is the discriminator that matters most on a rejection:
 * 'vendor' means SumSub refused the documents, 'system' means our identity gate
 * found the verified person is not the owner the seller declared. Those are very
 * different events and collapsing them into one "Rejected" label would have ops
 * reading an attempted impersonation as a bad passport scan.
 */
export function kycDecisionLabel(row: PropertyOwnerKycRow): string {
  if (row.kycStatus === 'approved') {
    if (row.decisionSource === 'vendor') return 'Auto-approved · identity gate passed'
    if (row.decisionSource === 'admin') return 'Approved by admin'
    return 'Approved'
  }
  if (row.kycStatus === 'rejected') {
    if (row.decisionSource === 'system') return 'Rejected · identity mismatch'
    if (row.decisionSource === 'vendor') return 'Rejected by vendor'
    if (row.decisionSource === 'admin') return 'Rejected by admin'
    return 'Rejected'
  }
  if (row.kycStatus === 'expired') return 'Expired'
  return '—'
}
